<template>
	<div class="album">
		<Header
			:title="title"
			:filters="filters"
			@changeSearch="handleSearch"
		/>
		<div
			v-if="filtersData.search.value"
			class="album__search-text"
		>
			{{ $I18n.trans('arena.match_center.results_for') }} "{{ filtersData.search.value }}"
		</div>
		<div
			v-if="noSearchResults"
			class="album__no-results"
		>
			{{ $I18n.trans('arena.match_center.no_results') }}
		</div>
		<EventsSection
			v-show="eventsSectionVisible"
			:title="didSearch ? $I18n.trans_choice('commons.event', 2): ''"
			class="album__events"
			:pagination="events.pagination"
			:handle-pagination-change="handlePaginationChange"
			:events="events.items"
			:is-loading="events.isLoading"
			:show-results-counter="filtersData.search.value !== '' && typeof events.pagination.total === 'number'"
		/>
	</div>
</template>
<script>
import Header from '@js/components/VideoCenter/Header';
import EventsSection from '@js/components/EventListing/EventsSection';
import { EventFilter } from '@shared/enums/EventFilter';
import { debounce } from '@shared/utils/debounce';
import { httpClient } from '@shared/js/api/httpClient';

export default {
	name:       'Album',
	components: { EventsSection, Header },
	props:      {
		albumId: {
			type:     Number,
			required: true,
		},
		title: {
			type:    String,
			default: null,
		},
		type: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			filtersData: {
				search: {
					value: '',
				},
			},
			events: {
				isLoading:  false,
				pagination: this.getInitialPagination(),
				items:      [],
			},
			debounceSearch: null,
		};
	},
	computed: {
		filters() {
			return [
				{
					type:         'search',
					id:           'search',
					defaultValue: '',
					emitEvent:    'changeSearch',
					...this.filtersData.search,
				},
			];
		},
		eventsSectionVisible() {
			return !this.noSearchResults || this.events.isLoading;
		},
		noSearchResults() {
			const noResultsOnVideos = this.events.items.length === 0 && !this.events.isLoading;
			return this.didSearch && noResultsOnVideos;
		},
		didSearch() {
			return !!this.filtersData.search.value;
		},
	},
	created() {
		this.debounceSearch = debounce(this.getEvents, 300, false);
	},
	mounted() {
		this.getEvents();
	},
	methods: {
		handleSearch(value) {
			this.filtersData.search.value = value;
			this.events.pagination = this.getInitialPagination(12);
			this.debounceSearch();
		},
		handlePaginationChange(page) {
			this.events.pagination.currentPage = page;
			this.getEvents();
		},
		async getEvents() {
			try {
				this.events.isLoading = true;

				const searchParams = this.getSearchParams();
				const data = await this.fetchData(searchParams);
				this.events.items = data.data;
				this.events.pagination = data.meta;
			} catch (error) {
				// shows empty list
			} finally {
				this.events.isLoading = false;
			}
		},
		async fetchData(searchParams) {
			const response = await httpClient.getEventsCached(Object.fromEntries(new URLSearchParams(searchParams)));
			return response.data;
		},
		getSearchParams() {
			const searchParams = new URLSearchParams();

			const filters = {
				[EventFilter.ARENA_ALBUM]: this.albumId,
			};

			if (this.filtersData.search.value) {
				filters[EventFilter.SEARCH] = this.filtersData.search.value;
			}

			// filter parameters
			searchParams.append('filters', JSON.stringify(filters));
			searchParams.append('page', JSON.stringify(this.events.pagination.currentPage));
			searchParams.append('perPage', JSON.stringify(this.events.pagination.perPage));

			return searchParams;
		},
		getInitialPagination(perPage = 12) {
			return {
				perPage:     perPage,
				currentPage: 1,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.album {
	padding-top: $sp6;
}

.album__search-text {
	@include font(primary, light, normal, fs-180);
	margin-bottom: $sp7;
}

.album__no-results {
	@include font(primary, light, normal, fs-150);
	color: var(--color-grey-300);
}
</style>
